/**
 * Pictures to start from. The photographs and paintings are public domain works served
 * from `public/samples`; the letter is drawn on the fly so there is always a high-contrast
 * logo-like target at hand.
 */
export interface SampleSource {
  id: string;
  label: string;
  /** Who made the picture, or what the drawing is for; shown under the card. */
  hint: string;
  /** File name the editor shows once the sample is loaded. */
  file: string;
  /** A static picture under `public/`. */
  src?: string;
  /** A drawing produced on demand, when there is no picture. */
  draw?: (ctx: CanvasRenderingContext2D, size: number) => void;
}

export const SAMPLES: SampleSource[] = [
  {
    id: 'girl',
    label: 'Girl with a pearl',
    hint: 'Johannes Vermeer, about 1665',
    file: 'Girl with a Pearl Earring.jpg',
    src: '/samples/girl-with-a-pearl-earring.jpg',
  },
  {
    id: 'julia-jackson',
    label: 'Julia Jackson',
    hint: 'Julia Margaret Cameron, 1867',
    file: 'Julia Jackson.jpg',
    src: '/samples/julia-jackson.jpg',
  },
  {
    id: 'hare',
    label: 'Young hare',
    hint: 'Albrecht Dürer, 1502',
    file: 'Young Hare.jpg',
    src: '/samples/young-hare.jpg',
  },
  {
    id: 'letter-a',
    label: 'Letter A',
    hint: 'High contrast, like a logo',
    file: 'Letter A.png',
    draw(ctx, size) {
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, size, size);
      ctx.fillStyle = '#111';
      ctx.font = `bold ${Math.round(size * 0.82)}px Georgia, "Times New Roman", serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('A', size / 2, size * 0.56);
    },
  },
];

/** The sample as a file the editor can decode: fetched from `public/`, or drawn at `size` px. */
export async function renderSample(sample: SampleSource, size = 1024): Promise<Blob> {
  if (sample.src) {
    const res = await fetch(sample.src);
    if (!res.ok) throw new Error(`The sample "${sample.label}" could not be loaded (${res.status}).`);
    return res.blob();
  }
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D is not available.');
  sample.draw?.(ctx, size);
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('sample rendering failed'))), 'image/png');
  });
}

/** A small preview of the sample for a card: the picture itself, or the drawing at `size` px. */
export function sampleThumb(sample: SampleSource, size: number): HTMLElement {
  if (sample.src) {
    const img = document.createElement('img');
    img.src = sample.src;
    img.alt = '';
    img.loading = 'lazy';
    img.decoding = 'async';
    return img;
  }
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  canvas.setAttribute('aria-hidden', 'true');
  const ctx = canvas.getContext('2d');
  if (ctx) sample.draw?.(ctx, size);
  return canvas;
}
